import React, { useState, useRef, useEffect } from 'react';

const GridCell = ({ content, onClick, onUpdate, editorMode, isArrowStart, className, id, rowId, colId, onOpenSubEditor }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [cellContent, setCellContent] = useState(content || '');
  const [isHovered, setIsHovered] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [hasLongContent, setHasLongContent] = useState(false);
  // États pour le redimensionnement
  const [cellHeight, setCellHeight] = useState(null);
  const [cellWidth, setCellWidth] = useState(null);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeDirection, setResizeDirection] = useState(null); // 'vertical' ou 'horizontal'
  
  const inputRef = useRef(null);
  const cellRef = useRef(null);
  const contentRef = useRef(null);

  // Mettre à jour le contenu local lorsque la prop content change
  useEffect(() => {
    setCellContent(content || '');
  }, [content]);

  // Détecter les contenus longs qui pourraient nécessiter un bouton d'expansion
  useEffect(() => {
    if (contentRef.current && content) {
      // Vérifier si le contenu dépasse la taille visible de la cellule
      const isContentOverflowing = 
        content.length > 150 || // Texte très long
        content.split('\n').length > 3 || // Plusieurs lignes
        (contentRef.current.scrollHeight > contentRef.current.clientHeight || 
         contentRef.current.scrollWidth > contentRef.current.clientWidth);
         
      setHasLongContent(isContentOverflowing);
    }
  }, [content, contentRef]);

  // Focus sur l'input lors de l'entrée en mode édition
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  // Animer la cellule après modification
  useEffect(() => {
    if (!isEditing && cellRef.current && cellContent !== content) {
      cellRef.current.classList.add('animate-pulse');
      const timer = setTimeout(() => {
        if (cellRef.current) {
          cellRef.current.classList.remove('animate-pulse');
        }
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [isEditing, content, cellContent]);

  // Déterminer si la cellule est éditable
  const canEdit = editorMode === 'cell';

  // Gérer le clic sur la cellule
  const handleCellClick = () => {
    if (canEdit) {
      setIsEditing(true);
    } else {
      onClick();
    }
  };

  // Gérer l'enregistrement des modifications
  const handleSave = () => {
    if (cellContent !== content) {
      onUpdate(cellContent);
    }
    setIsEditing(false);
  };

  // Gérer la touche Entrée pendant l'édition
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      setIsEditing(false);
      setCellContent(content || '');
    }
  };

  // Basculer l'état d'expansion
  const toggleExpand = (e) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  // Fonctions pour le redimensionnement vertical des cellules
  const handleResizeVerticalStart = (e) => {
    e.stopPropagation();
    e.preventDefault();
    
    if (!cellRef.current) return;
    
    setIsResizing(true);
    setResizeDirection('vertical');
    const startY = e.clientY;
    const startHeight = cellRef.current.offsetHeight;
    
    const handleMouseMove = (moveEvent) => {
      const newHeight = startHeight + (moveEvent.clientY - startY);
      if (newHeight >= 40) { // Hauteur minimale
        setCellHeight(newHeight);
        if (cellRef.current) {
          cellRef.current.style.height = `${newHeight}px`;
          cellRef.current.classList.add('has-custom-height');
        }
      }
    };
    
    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      setIsResizing(false);
      setResizeDirection(null);
      
      // Enregistrer la hauteur dans localStorage pour cette cellule spécifique
      if (cellHeight) {
        const cellSizes = JSON.parse(localStorage.getItem('graiCellSizes') || '{}');
        cellSizes[`${rowId}-${colId}`] = { 
          ...(cellSizes[`${rowId}-${colId}`] || {}),
          height: cellHeight 
        };
        localStorage.setItem('graiCellSizes', JSON.stringify(cellSizes));
      }
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };
  
  // Fonctions pour le redimensionnement horizontal des cellules
  const handleResizeHorizontalStart = (e) => {
    e.stopPropagation();
    e.preventDefault();
    
    if (!cellRef.current) return;
    
    setIsResizing(true);
    setResizeDirection('horizontal');
    const startX = e.clientX;
    const startWidth = cellRef.current.offsetWidth;
    
    const handleMouseMove = (moveEvent) => {
      const newWidth = startWidth + (moveEvent.clientX - startX);
      if (newWidth >= 100) { // Largeur minimale
        setCellWidth(newWidth);
        if (cellRef.current) {
          cellRef.current.style.width = `${newWidth}px`;
          cellRef.current.style.minWidth = `${newWidth}px`;
          cellRef.current.style.maxWidth = `${newWidth}px`;
          cellRef.current.classList.add('has-custom-width');
        }
      }
    };
    
    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      setIsResizing(false);
      setResizeDirection(null);
      
      // Enregistrer la largeur dans localStorage pour cette cellule spécifique
      if (cellWidth) {
        const cellSizes = JSON.parse(localStorage.getItem('graiCellSizes') || '{}');
        cellSizes[`${rowId}-${colId}`] = { 
          ...(cellSizes[`${rowId}-${colId}`] || {}),
          width: cellWidth 
        };
        localStorage.setItem('graiCellSizes', JSON.stringify(cellSizes));
      }
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };
  
  // Charger les dimensions sauvegardées au montage
  useEffect(() => {
    const cellSizes = JSON.parse(localStorage.getItem('graiCellSizes') || '{}');
    const savedSize = cellSizes[`${rowId}-${colId}`];
    
    if (savedSize && cellRef.current) {
      if (savedSize.height) {
        setCellHeight(savedSize.height);
        cellRef.current.style.height = `${savedSize.height}px`;
        cellRef.current.classList.add('has-custom-height');
      }
      
      if (savedSize.width) {
        setCellWidth(savedSize.width);
        cellRef.current.style.width = `${savedSize.width}px`;
        cellRef.current.style.minWidth = `${savedSize.width}px`;
        cellRef.current.style.maxWidth = `${savedSize.width}px`;
        cellRef.current.classList.add('has-custom-width');
      }
    }
  }, [rowId, colId]);

  // Réinitialiser les dimensions
  const resetDimensions = (e, type) => {
    e.stopPropagation();
    
    if (!cellRef.current) return;
    
    const cellSizes = JSON.parse(localStorage.getItem('graiCellSizes') || '{}');
    const existingSize = cellSizes[`${rowId}-${colId}`] || {};
    
    if (type === 'height' || type === 'all') {
      setCellHeight(null);
      cellRef.current.style.height = '';
      cellRef.current.classList.remove('has-custom-height');
      delete existingSize.height;
    }
    
    if (type === 'width' || type === 'all') {
      setCellWidth(null);
      cellRef.current.style.width = '';
      cellRef.current.style.minWidth = '';
      cellRef.current.style.maxWidth = '';
      cellRef.current.classList.remove('has-custom-width');
      delete existingSize.width;
    }
    
    // Mettre à jour ou supprimer l'entrée dans localStorage
    if (Object.keys(existingSize).length > 0) {
      cellSizes[`${rowId}-${colId}`] = existingSize;
    } else {
      delete cellSizes[`${rowId}-${colId}`];
    }
    
    localStorage.setItem('graiCellSizes', JSON.stringify(cellSizes));
  };

  // Déterminer les classes CSS
  const cellClasses = [
    'grid-cell',
    canEdit ? 'editable' : '',
    isArrowStart ? 'is-arrow-start' : '',
    editorMode === 'arrow' ? 'arrow-mode' : '',
    isHovered && canEdit ? 'cell-hovered' : '',
    hasLongContent ? 'has-long-content' : '',
    isExpanded ? 'expanded' : '',
    isResizing ? `resizing resizing-${resizeDirection}` : '',
    cellHeight ? 'has-custom-height' : '',
    cellWidth ? 'has-custom-width' : '',
    className || ''
  ].filter(Boolean).join(' ');

  // Gérer le survol
  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  // Contenu de la cellule
  const cellContentElement = isEditing ? (
    <textarea
      ref={inputRef}
      value={cellContent}
      onChange={(e) => setCellContent(e.target.value)}
      onBlur={handleSave}
      onKeyDown={handleKeyDown}
      className="cell-input"
      onClick={(e) => e.stopPropagation()}
      placeholder="Entrez le contenu ici..."
    />
  ) : (
    <div className="grid-cell-content" ref={contentRef}>
      {cellContent ? 
        cellContent.split('\n').map((line, index) => (
          <React.Fragment key={index}>
            {line}
            {index < cellContent.split('\n').length - 1 && <br />}
          </React.Fragment>
        )) : 
        canEdit && (
          <div className="empty-cell-indicator">
            {isHovered && (
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path>
              </svg>
            )}
          </div>
        )
      }
    </div>
  );

  return (
  <div 
    ref={cellRef}
    className={cellClasses} 
    onClick={handleCellClick}
    onMouseEnter={handleMouseEnter}
    onMouseLeave={handleMouseLeave}
    title={canEdit ? "Cliquez pour éditer" : editorMode === 'arrow' ? "Cliquez pour créer une relation" : ""}
    id={id}
  >
    {cellContentElement}
    
    {/* Bouton d'édition au survol */}
    {isHovered && canEdit && !isEditing && (
      <div className="cell-edit-indicator">
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path>
        </svg>
      </div>
    )}
    
    {/* Bouton d'expansion pour les contenus longs */}
    {!isEditing && hasLongContent && (
      <button 
        className="cell-expand-button" 
        onClick={toggleExpand}
        title={isExpanded ? "Réduire" : "Afficher tout le contenu"}
      >
        {isExpanded ? "−" : "+"}
      </button>
    )}
    
    {/* Poignée de redimensionnement vertical */}
    {canEdit && !isEditing && (
      <div 
        className="cell-resize-handle-bottom"
        onMouseDown={handleResizeVerticalStart}
        title="Redimensionner la hauteur"
      >
        <div className="resize-handle-indicator"></div>
      </div>
    )}
    
    {/* Poignée de redimensionnement horizontal */}
    {canEdit && !isEditing && (
      <div 
        className="cell-resize-handle-right"
        onMouseDown={handleResizeHorizontalStart}
        title="Redimensionner la largeur"
      >
        <div className="resize-handle-indicator"></div>
      </div>
    )}
    
    {/* Menu de réinitialisation des dimensions */}
    {canEdit && !isEditing && (cellHeight || cellWidth) && (
      <div className="cell-dimensions-controls">
        {cellHeight && (
          <button 
            className="cell-reset-size reset-height" 
            onClick={(e) => resetDimensions(e, 'height')}
            title="Réinitialiser la hauteur"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 2v6h6"></path>
              <path d="M3 13a9 9 0 1 0 3-7.7L3 8"></path>
            </svg>
            <span className="dimension-icon">↕</span>
          </button>
        )}
        
        {cellWidth && (
          <button 
            className="cell-reset-size reset-width" 
            onClick={(e) => resetDimensions(e, 'width')}
            title="Réinitialiser la largeur"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 2v6h6"></path>
              <path d="M3 13a9 9 0 1 0 3-7.7L3 8"></path>
            </svg>
            <span className="dimension-icon">↔</span>
          </button>
        )}
        
        {(cellHeight && cellWidth) && (
          <button 
            className="cell-reset-size reset-all" 
            onClick={(e) => resetDimensions(e, 'all')}
            title="Réinitialiser toutes les dimensions"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 2v6h6"></path>
              <path d="M3 13a9 9 0 1 0 3-7.7L3 8"></path>
            </svg>
          </button>
        )}
      </div>
    )}

    {/* ✅ Bouton pour ouvrir le sous-éditeur (ajouté) */}
    {isHovered && !isEditing && (
      <button
        className="cell-subeditor-button"
        onClick={(e) => {
          e.stopPropagation();
          if (onOpenSubEditor) onOpenSubEditor(rowId, colId);
        }}
        title="Ouvrir le sous-éditeur"
      >
        +
      </button>
    )}
  </div>
  );

};

export default GridCell; 